import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MessageSquareWarning } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
  describeElement, findMeaningfulTarget, targetFromPoint, type PickedElement,
} from "@/lib/requests/element-picker";
import { collectRequestContext } from "@/lib/requests/context";
import { computeLift } from "@/lib/requests/lift";
import { useRequestQueue } from "@/hooks/useRequestQueue";
import { useRequestSubmit } from "@/hooks/useRequestSubmit";
import { RequestComposer } from "./RequestComposer";
import { PickModeBanner } from "./PickModeBanner";

const TAP_SLOP = 12;
const COMPOSER_MOUNTED = '[data-testid="request-input"]';

function insidePicker(target: EventTarget | null): boolean {
  return target instanceof Element && !!target.closest("[data-requests]");
}

/** The ONLY hook in this component. No state, no effects, no queue, no mutation. */
export function RequestButton() {
  const { isSuperAdmin } = useAuth();
  if (!isSuperAdmin) return null;
  return <RequestButtonInner />; // remounts cleanly when the role resolves
}

/** Every other hook lives here, unconditionally — only ever mounted post-gate. */
function RequestButtonInner() {
  const { pending, refresh } = useRequestQueue();
  const submit = useRequestSubmit();

  const [open, setOpen] = useState(false);
  const [picking, setPicking] = useState(false);
  const [message, setMessage] = useState("");
  const [pick, setPick] = useState<PickedElement | null>(null);
  const [lift, setLift] = useState(0);

  const touchStartRef = useRef<{ x: number; y: number } | null>(null);

  // ── Collision-avoidance lift ──────────────────────────────────────────
  const remeasure = useCallback(() => {
    const size = 48;
    const gap = 16;
    const probeX = window.innerWidth - gap - size / 2;
    const probeY = window.innerHeight - gap - size / 2;
    const stack = document.elementsFromPoint(probeX, probeY);
    setLift(computeLift(stack, window.innerHeight));
  }, []);

  useEffect(() => {
    let raf = 0;
    const scheduled = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(remeasure);
    };
    scheduled();

    const observer = new MutationObserver(scheduled);
    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener("resize", scheduled);
    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
      window.removeEventListener("resize", scheduled);
    };
  }, [open, picking, remeasure]);

  const resetComposer = () => {
    setMessage("");
    setPick(null);
  };

  const startPicking = useCallback(() => {
    setOpen(false);
    const deadline = performance.now() + 800;
    const armWhenClear = () => {
      if (!document.querySelector(COMPOSER_MOUNTED) || performance.now() > deadline) {
        setPicking(true);
        return;
      }
      requestAnimationFrame(armWhenClear);
    };
    requestAnimationFrame(armWhenClear);
  }, []);

  const pickAt = useCallback((clientX: number, clientY: number) => {
    const hit = targetFromPoint({ clientX, clientY, target: null });
    if (!hit) {
      setPicking(false);
      setOpen(true);
      return;
    }
    const el = findMeaningfulTarget(hit);
    const picked = describeElement(el, { x: clientX + window.scrollX, y: clientY + window.scrollY });
    setPick(picked);
    setPicking(false);
    setOpen(true);
  }, []);

  // ── Pick-mode listeners ────────────────────────────────────────────────
  useEffect(() => {
    if (!picking) return;

    const onPointerDown = (e: PointerEvent) => {
      if (insidePicker(e.target)) return;
      if (e.pointerType === "touch") {
        e.stopPropagation();
        return; // touch is decided at touchend
      }
      e.preventDefault();
      e.stopPropagation();
      pickAt(e.clientX, e.clientY);
    };
    const swallow = (e: MouseEvent) => {
      if (insidePicker(e.target)) return;
      e.stopPropagation(); // deliberately no preventDefault — cancelling pointerdown already suppresses compat mouse events
    };
    const onClick = (e: MouseEvent) => {
      if (insidePicker(e.target)) return;
      e.preventDefault();
      e.stopPropagation();
    };
    const onTouchStart = (e: TouchEvent) => {
      if (insidePicker(e.target)) return;
      e.stopPropagation();
      const t = e.touches[0];
      touchStartRef.current = { x: t.clientX, y: t.clientY };
    };
    const onTouchEnd = (e: TouchEvent) => {
      if (insidePicker(e.target)) return;
      const t = e.changedTouches[0];
      const start = touchStartRef.current;
      touchStartRef.current = null;
      if (start && Math.hypot(t.clientX - start.x, t.clientY - start.y) > TAP_SLOP) {
        return; // that was a scroll — stay armed
      }
      e.preventDefault(); // suppresses the synthesized click that would double-pick
      e.stopPropagation();
      pickAt(t.clientX, t.clientY);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setPicking(false);
        setOpen(true);
      }
    };

    document.addEventListener("pointerdown", onPointerDown, { capture: true });
    document.addEventListener("mousedown", swallow, true);
    document.addEventListener("click", onClick, true);
    document.addEventListener("touchstart", onTouchStart, { capture: true });
    document.addEventListener("touchend", onTouchEnd, { capture: true });
    document.addEventListener("keydown", onKeyDown, true);
    document.body.style.cursor = "crosshair";

    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("mousedown", swallow, true);
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("touchstart", onTouchStart, true);
      document.removeEventListener("touchend", onTouchEnd, true);
      document.removeEventListener("keydown", onKeyDown, true);
      document.body.style.cursor = "";
    };
  }, [picking, pickAt]);

  const handleSubmit = () => {
    const context = collectRequestContext();
    submit.mutate(
      {
        message,
        pick,
        context,
        pageName: document.title || context.page_path,
      },
      {
        onSettled: () => {
          resetComposer();
          setOpen(false);
          refresh();
        },
      },
    );
  };

  return (
    <>
      {createPortal(
        <button
          type="button"
          data-requests
          data-testid="request-trigger"
          onClick={startPicking}
          onPointerDownCapture={(e) => e.stopPropagation()}
          className="pointer-events-auto fixed right-4 z-[70] flex h-12 w-12 items-center justify-center rounded-full bg-secondary text-secondary-foreground shadow-lg transition-colors hover:bg-secondary/90"
          style={{ bottom: `calc(max(1rem, env(safe-area-inset-bottom)) + ${lift}px)` }}
          aria-label="Capture a request"
        >
          {pending > 0 ? (
            <span className="relative">
              <MessageSquareWarning className="h-5 w-5" />
              <span
                data-testid="request-pending-dot"
                className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-destructive"
              />
            </span>
          ) : (
            <MessageSquareWarning className="h-5 w-5" />
          )}
        </button>,
        document.body,
      )}

      {picking && <PickModeBanner onCancel={() => { setPicking(false); setOpen(true); }} />}

      <RequestComposer
        open={open}
        onOpenChange={(v) => {
          if (!v) resetComposer();
          setOpen(v);
        }}
        message={message}
        onMessageChange={setMessage}
        pick={pick}
        onClearPick={() => setPick(null)}
        onStartPicking={startPicking}
        pendingCount={pending}
        isPending={submit.isPending}
        onSubmit={handleSubmit}
      />
    </>
  );
}
